import * as React from 'react';
import { observer, Observer } from 'mobx-react-lite';
import { Modal, Table, Select, Button } from 'antd';
import { TrainingProjectsStore } from '../../stores';
import { ModelSelectionOption } from '../../types';
import ImportSummarySection from './ImportSummarySection';
import './ImportTrainingProjectModal.less';

type Props = {
    store: TrainingProjectsStore;
};

const ImportTrainingProjectModal: React.FC<Props> = ({ store }) => {
    const onCancel = () => {
        store.setImportDialogVisible(false);
        store.resetImportData();
    };

    const onSubmit = () => {
        store.commitImportedProject();
    };

    const handleModelChange = (value: string, modelType: 'reference' | 'teacher' | 'judge') => {
        switch (modelType) {
            case 'reference':
                store.setSelectedReferenceModelId(value);
                break;
            case 'teacher':
                store.setSelectedTeacherModelId(value);
                break;
            case 'judge':
                store.setSelectedJudgeModelId(value);
                break;
            default:
                break;
        }
    };

    if (!store.importData) {
        return null;
    }

    const modelColumns = [
        {
            title: 'Model',
            dataIndex: 'model',
            key: 'model'
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            width: 200
        },
        {
            title: '',
            dataIndex: 'select',
            key: 'select',
            width: 500,
            render: (_: unknown, record: { modelType: 'reference' | 'teacher' | 'judge' }) => {
                const getSelectedModelId = () => {
                    switch (record.modelType) {
                        case 'reference':
                            return store.selectedReferenceModelId || undefined;
                        case 'teacher':
                            return store.selectedTeacherModelId || undefined;
                        case 'judge':
                            return store.selectedJudgeModelId || undefined;
                        default:
                            return undefined;
                    }
                };

                return (
                    <Observer>
                        {() => (
                            <Select<string>
                                style={{ width: '100%' }}
                                placeholder="Select a model"
                                value={getSelectedModelId()}
                                onChange={value => handleModelChange(value, record.modelType)}
                                options={store.importData?.modelSelectionOptions.map(
                                    (option: ModelSelectionOption) => ({
                                        label: option.label,
                                        value: option.value,
                                        title: option.label
                                    })
                                )}
                                showSearch
                                filterOption={(text, option) =>
                                    option?.title && option.title.toLowerCase().includes(text.toLowerCase())
                                }
                                allowClear={record.modelType !== 'reference'}
                            />
                        )}
                    </Observer>
                );
            }
        }
    ];

    const modelData = [
        {
            key: '1',
            model: store.importData?.importedData?.referenceModelName
                ? `Reference Model (${store.importData.importedData.referenceModelName})`
                : 'Reference Model',
            type: 'LLM',
            modelType: 'reference' as const
        },
        {
            key: '2',
            model: store.importData?.importedData?.teacherModelName
                ? `Teacher Model (${store.importData.importedData.teacherModelName})`
                : 'Teacher Model',
            type: 'LLM',
            modelType: 'teacher' as const
        },
        {
            key: '3',
            model: store.importData?.importedData?.judgeModelName
                ? `Judge Model (${store.importData.importedData.judgeModelName})`
                : 'Judge Model',
            type: 'LLM',
            modelType: 'judge' as const
        }
    ];

    return (
        <Modal
            title="Import Training Project"
            visible={store.importDialogVisible}
            onCancel={onCancel}
            maskClosable={false}
            destroyOnClose={false}
            closable={false}
            width={1300}
            className="alpha-portal-dialog rule-import table scrollable full-screen"
            centered
            footer={[
                <Button
                    data-id="import-training-project-cancel"
                    size="large"
                    className="light"
                    key="back"
                    onClick={onCancel}
                >
                    Cancel
                </Button>,
                <Button
                    className="dark"
                    data-id="import-training-project-submit"
                    size="large"
                    key="submit"
                    type="primary"
                    onClick={onSubmit}
                    disabled={!store.selectedReferenceModelId}
                >
                    Submit
                </Button>
            ]}
        >
            <div className="model-selection-section">
                <h3>Model Selection</h3>
                <Table
                    rowKey={record => record.modelType}
                    columns={modelColumns}
                    dataSource={modelData}
                    pagination={false}
                />
            </div>

            <ImportSummarySection store={store} />
        </Modal>
    );
};

export default observer(ImportTrainingProjectModal);
